// ============================================================================
// DAS TICKET SERVICE
// ============================================================================
// Business logic and API calls for DAS (Digital Approval System).

import { get, post, put, del } from '../client/axiosClient';
import { dasTicketEndpoints, requestEndpoints } from '../endpoints';
import { buildEndpointWithQuery } from '../endpoints/base.endpoints';
import type {
  ApiResponse,
  ListResponse,
  EmptyResponse,
  ID,
  DASTicket,
  DASTicketListItem,
  DASTicketDetails,
  CreateDASTicketRequest,
  UpdateDASTicketRequest,
  ApprovalActionRequest,
  DASTicketFilterParams,
  DashboardStats,
  WorkflowAction,
  ApprovalLevel,
  TakeActionRequest,
  TakeActionResponse,
  InsertAdditionalApproverRequest,
  InsertAdditionalApproverResponse,
  InsertMultipleAdditionalApproversRequest,
  InsertMultipleAdditionalApproversResponse,
  ProcessDynamicApprovalRequest,
  AdditionalApprover,
  ApprovalPosition,
  RelatedTicketDisplay,
  ApprovalLevelWithDynamic,
} from '../types';

/**
 * DAS Ticket Service
 * Handles all API operations related to Digital Approval System tickets
 */
class DASTicketService {
  // ============================================================================
  // LIST & FILTER OPERATIONS
  // ============================================================================

  /**
   * Get all tickets with optional filters
   */
  async getAll(params?: DASTicketFilterParams): Promise<ListResponse<DASTicketListItem>> {
    const endpoint = params
      ? buildEndpointWithQuery(dasTicketEndpoints.list(), params)
      : dasTicketEndpoints.list();

    const response = await get<ListResponse<DASTicketListItem>>(endpoint);
    return response.data;
  }

  /**
   * Get tickets for "My Active Items" tab
   */
  async getMyActiveItems(params?: DASTicketFilterParams): Promise<ListResponse<DASTicketListItem>> {
    const endpoint = params
      ? buildEndpointWithQuery(dasTicketEndpoints.myActiveItems(), params)
      : dasTicketEndpoints.myActiveItems();

    const response = await get<ListResponse<DASTicketListItem>>(endpoint);
    return response.data;
  }

  /**
   * Get tickets for "My Pending Approvals" tab
   */
  async getMyPendingApprovals(params?: DASTicketFilterParams): Promise<ListResponse<DASTicketListItem>> {
    const endpoint = params
      ? buildEndpointWithQuery(dasTicketEndpoints.myPendingApprovals(), params)
      : dasTicketEndpoints.myPendingApprovals();

    const response = await get<ListResponse<DASTicketListItem>>(endpoint);
    return response.data;
  }

  /**
   * Get tickets created by current user
   */
  async getMyCreatedRequests(params?: DASTicketFilterParams): Promise<ListResponse<DASTicketListItem>> {
    const endpoint = params
      ? buildEndpointWithQuery(dasTicketEndpoints.myCreatedRequests(), params)
      : dasTicketEndpoints.myCreatedRequests();

    const response = await get<ListResponse<DASTicketListItem>>(endpoint);
    return response.data;
  }

  /**
   * Get completed tickets that are in progress
   */
  async getMyCompletedInProgress(params?: DASTicketFilterParams): Promise<ListResponse<DASTicketListItem>> {
    const endpoint = params
      ? buildEndpointWithQuery(dasTicketEndpoints.myCompletedInProgress(), params)
      : dasTicketEndpoints.myCompletedInProgress();

    const response = await get<ListResponse<DASTicketListItem>>(endpoint);
    return response.data;
  }

  /**
   * Get completed and closed tickets
   */
  async getMyCompletedClosed(params?: DASTicketFilterParams): Promise<ListResponse<DASTicketListItem>> {
    const endpoint = params
      ? buildEndpointWithQuery(dasTicketEndpoints.myCompletedClosed(), params)
      : dasTicketEndpoints.myCompletedClosed();

    const response = await get<ListResponse<DASTicketListItem>>(endpoint);
    return response.data;
  }

  /**
   * Get tickets escalated to current user
   */
  async getEscalatedToMe(params?: DASTicketFilterParams): Promise<ListResponse<DASTicketListItem>> {
    const endpoint = params
      ? buildEndpointWithQuery(dasTicketEndpoints.escalatedToMe(), params)
      : dasTicketEndpoints.escalatedToMe();

    const response = await get<ListResponse<DASTicketListItem>>(endpoint);
    return response.data;
  }

  /**
   * Get all tickets (admin view)
   */
  async getAllTickets(params?: DASTicketFilterParams): Promise<ListResponse<DASTicketListItem>> {
    const endpoint = params
      ? buildEndpointWithQuery(dasTicketEndpoints.allTickets(), params)
      : dasTicketEndpoints.allTickets();

    const response = await get<ListResponse<DASTicketListItem>>(endpoint);
    return response.data;
  }

  // ============================================================================
  // SINGLE TICKET OPERATIONS
  // ============================================================================

  /**
   * Get ticket by ID (basic info)
   */
  async getById(id: ID): Promise<ApiResponse<DASTicket>> {
    const response = await get<ApiResponse<DASTicket>>(dasTicketEndpoints.getById(id));
    return response.data;
  }

  /**
   * Get ticket details (full info with workflow, attachments, etc.)
   * Integrates with the real Request API
   */
  async getDetails(id: ID): Promise<ApiResponse<DASTicketDetails>> {
    // Use the real Request API details endpoint (includes /v1/ prefix)
    // getDetails returns full data with approvers, attachments, and related tickets
    const response = await get<any>(requestEndpoints.getDetails(id));

    // Map Request to DASTicketDetails format
    const request = response.data.data;

    // DEBUG: Log the raw response to check if value and requiredByDate are present
    console.log('📋 TICKET DETAILS RAW RESPONSE:', {
      requestValue: request.requestValue,
      requiredByDate: request.requiredByDate,
      fullRequest: request,
    });

    // Map approvers to approval hierarchy
    const approvalHierarchy = this.mapApproversToHierarchy(request.approvers || []);

    // Map attachments to ticket attachment format
    const attachments = this.mapAttachments(request.attachments || []);

    // Related tickets - for now showing GUIDs, ideally we'd fetch request numbers
    const relatedTickets = (request.relatedTicketIds || []).map((id: string) => id);

    const ticketDetails: DASTicketDetails = {
      id: request.id,
      ticketNo: request.requestNumber,
      subject: request.title,
      description: request.description || '',
      requestType: this.mapRequestTypeName(request.requestTypeName) || 'General Request',
      yourRole: 'Creator', // TODO: Determine from workflow
      currentStage: this.mapStatusToStage(request.currentStatus, request.currentWorkflowStep),
      yourActionStatus: 'No Action Required', // TODO: Determine from workflow
      priority: this.mapPriority(request.priority) || 'Normal',
      status: request.currentStatus === 'Approved' || request.currentStatus === 'Rejected' ? 'Archived' : 'Active',
      pendingSince: request.submittedAt,
      pendingAge: this.calculatePendingAge(request.submittedAt),
      ticketAge: this.calculatePendingAge(request.createdAt),
      createdAt: request.createdAt,
      createdBy: request.createdBy,
      createdByUserId: request.submittedBy,
      createdByUserName: request.submittedByName || 'Unknown',
      workflowHistory: [], // TODO: Fetch from workflow history API
      approvalHierarchy,
      relatedTickets,
      attachments,
      documentCount: attachments.length,
      // Value and Required By Date fields
      value: request.requestValue,
      requiredByDate: request.requiredByDate,
      // Include rowVersion for optimistic concurrency control
      rowVersion: request.rowVersion?.toString(),
    };

    // DEBUG: Log the mapped ticket details
    console.log('📊 MAPPED TICKET DETAILS:', {
      value: ticketDetails.value,
      requiredByDate: ticketDetails.requiredByDate,
    });

    return {
      success: true,
      data: ticketDetails,
      message: 'Request details retrieved successfully',
    };
  }

  /**
   * Map backend approvers to UI approval hierarchy format
   */
  private mapApproversToHierarchy(approvers: any[]): ApprovalLevel[] {
    if (!approvers || approvers.length === 0) return [];

    return approvers.map((approver) => ({
      level: approver.sequenceOrder || 1,
      levelName: approver.levelName || `Level ${approver.sequenceOrder || 1}`,
      approverName: approver.approverUserName || approver.approverRoleName || 'Unknown',
      approverRole: approver.approverType === 'role'
        ? (approver.approverRoleName || 'Role')
        : (approver.levelName || 'Approver'),
      status: this.mapApprovalStatus(approver.approvalStatus),
      actionDate: approver.approvalAt,
      comments: approver.approvalComments,
    }));
  }

  /**
   * Map backend approval status to UI status
   */
  private mapApprovalStatus(status: string): 'Pending' | 'Approved' | 'Rejected' | 'Skipped' {
    if (!status) return 'Pending';
    const statusLower = status.toLowerCase();
    if (statusLower.includes('approved')) return 'Approved';
    if (statusLower.includes('rejected')) return 'Rejected';
    if (statusLower.includes('skipped')) return 'Skipped';
    return 'Pending';
  }

  /**
   * Map backend attachments to UI attachment format
   */
  private mapAttachments(attachments: any[]): any[] {
    if (!attachments || attachments.length === 0) return [];

    return attachments.map((att) => ({
      id: att.id,
      fileName: att.originalFileName || att.fileName,
      fileSize: att.fileSizeBytes || 0,
      fileType: att.mimeType || att.fileType || 'application/octet-stream',
      uploadedBy: att.uploadedByUserName || att.createdByName || 'Unknown',
      uploadedAt: att.uploadedAt || att.createdAt,
      downloadUrl: `/api/v1/attachments/${att.id}/download`,
    }));
  }

  // Helper methods for mapping
  private mapRequestTypeName(name?: string): any {
    const validTypes = ['Purchase Request', 'Leave Request', 'Travel Request', 'Budget Approval', 'Policy Exception', 'General Request'];
    return validTypes.includes(name || '') ? name : 'General Request';
  }

  private mapPriority(priority?: string): any {
    const priorityMap: Record<string, any> = {
      'Urgent': 'Critical',
      'High': 'High',
      'Medium': 'High',
      'Normal': 'Normal'
    };
    return priorityMap[priority || 'Normal'] || 'Normal';
  }

  private mapStatusToStage(status: string, currentWorkflowStep?: string): any {
    // Prefer currentWorkflowStep if available
    if (currentWorkflowStep) {
      return currentWorkflowStep;
    }
    // Otherwise fall back to status mapping
    const stageMap: Record<string, any> = {
      'Draft': 'Draft',
      'Submitted': 'Submitted',
      'In Review': 'In Review',
      'Approved': 'Approved',
      'Rejected': 'Rejected',
      'On Hold': 'On Hold',
      'Cancelled': 'Cancelled'
    };
    return stageMap[status] || status;
  }

  private calculatePendingAge(dateString: string): number {
    const date = new Date(dateString);
    const today = new Date();
    // Use Math.ceil to ensure same-day requests show as 1 day (consistent with requestToTicketMapper)
    return Math.ceil((today.getTime() - date.getTime()) / (1000 * 60 * 60 * 24));
  }

  // ============================================================================
  // CRUD OPERATIONS
  // ============================================================================

  /**
   * Create new ticket
   */
  async create(data: CreateDASTicketRequest): Promise<ApiResponse<DASTicket>> {
    const response = await post<ApiResponse<DASTicket>>(dasTicketEndpoints.create(), data);
    return response.data;
  }

  /**
   * Update existing ticket
   */
  async update(id: ID, data: UpdateDASTicketRequest): Promise<ApiResponse<DASTicket>> {
    const response = await put<ApiResponse<DASTicket>>(dasTicketEndpoints.update(id), data);
    return response.data;
  }

  /**
   * Delete ticket
   */
  async delete(id: ID): Promise<EmptyResponse> {
    const response = await del<EmptyResponse>(dasTicketEndpoints.delete(id));
    return response.data;
  }

  // ============================================================================
  // APPROVAL ACTIONS
  // ============================================================================

  /**
   * Approve a ticket
   */
  async approve(request: ApprovalActionRequest): Promise<ApiResponse<DASTicket>> {
    const response = await post<ApiResponse<DASTicket>>(
      dasTicketEndpoints.approve(request.ticketId),
      request
    );
    return response.data;
  }

  /**
   * Reject a ticket
   */
  async reject(request: ApprovalActionRequest): Promise<ApiResponse<DASTicket>> {
    const response = await post<ApiResponse<DASTicket>>(
      dasTicketEndpoints.reject(request.ticketId),
      request
    );
    return response.data;
  }

  /**
   * Recommend a ticket (positive or adverse)
   */
  async recommend(request: ApprovalActionRequest): Promise<ApiResponse<DASTicket>> {
    const response = await post<ApiResponse<DASTicket>>(
      dasTicketEndpoints.recommend(request.ticketId),
      request
    );
    return response.data;
  }

  /**
   * Escalate a ticket
   */
  async escalate(request: ApprovalActionRequest): Promise<ApiResponse<DASTicket>> {
    const response = await post<ApiResponse<DASTicket>>(
      dasTicketEndpoints.escalate(request.ticketId),
      request
    );
    return response.data;
  }

  /**
   * Return ticket for clarification
   */
  async returnForClarification(request: ApprovalActionRequest): Promise<ApiResponse<DASTicket>> {
    const response = await post<ApiResponse<DASTicket>>(
      dasTicketEndpoints.returnForClarification(request.ticketId),
      request
    );
    return response.data;
  }

  /**
   * Add comment to ticket
   */
  async addComment(ticketId: ID, comment: string): Promise<ApiResponse<void>> {
    const response = await post<ApiResponse<void>>(dasTicketEndpoints.addComment(ticketId), {
      comment,
    });
    return response.data;
  }

  // ============================================================================
  // TAKE ACTION (Enhanced Approval Actions)
  // ============================================================================

  /**
   * Take action on a ticket (Approve, Reject, Request Changes, Forward, Withdraw)
   * Routes to the appropriate approval actions service based on action type
   */
  async takeAction(request: TakeActionRequest): Promise<ApiResponse<TakeActionResponse>> {
    // Import approval actions service dynamically to avoid circular dependencies
    const { approvalActionsService } = await import('./das/approvalActionsService');
    const { attachmentsService } = await import('./das/attachmentsService');

    const ticketId = String(request.ticketId);
    const comments = request.comments || '';

    try {
      // Upload attachments first if any are provided
      if (request.attachments && request.attachments.length > 0) {
        console.log(`Uploading ${request.attachments.length} attachment(s) for ticket ${ticketId}`);

        for (const attachment of request.attachments) {
          try {
            await attachmentsService.upload(attachment.file, {
              requestId: ticketId,
              documentTitle: attachment.title || attachment.file.name,
              documentDescription: attachment.description,
            });
            console.log(`Uploaded attachment: ${attachment.title || attachment.file.name}`);
          } catch (uploadError) {
            console.error(`Failed to upload attachment: ${attachment.title}`, uploadError);
            // Continue with other attachments even if one fails
          }
        }
      }

      switch (request.action) {
        case 'approve':
          await approvalActionsService.approve(ticketId, {
            comments,
          });
          break;

        case 'reject':
          await approvalActionsService.reject(ticketId, {
            reason: comments || 'Rejected',
            comments,
          });
          break;

        case 'forward':
          // 'forward' maps to 'escalate' in the backend
          await approvalActionsService.escalate(ticketId, {
            reason: request.forwardReason || comments || 'Escalated',
            escalateToUserId: request.forwardToUserId ? String(request.forwardToUserId) : undefined,
            comments,
          });
          break;

        case 'request-changes':
          // 'request-changes' maps to 'return for clarification' in the backend
          // Import ReturnReasonCategory dynamically to avoid circular dependencies
          const { ReturnReasonCategory } = await import('../types/das/approvalActions.types');

          const returnRequest = {
            reasonCategory: request.returnReasonCategory || ReturnReasonCategory.ClarificationNeeded,
            clarificationRequired: request.clarificationRequired || comments || 'Clarification needed',
            specificQuestions: request.specificQuestions,
            responseDeadline: request.responseDeadline,
            comments,
            rowVersion: request.rowVersion || 0,
            // Pass the selected return recipient (can be original requestor or a previous approver)
            returnToUserId: request.returnToUserId,
          };

          await approvalActionsService.returnForClarification(ticketId, returnRequest);
          break;

        case 'withdraw':
          // TODO: Implement withdraw action in backend
          throw new Error('Withdraw action is not yet implemented');

        case 'add-approvers':
          // This is handled by dynamic approval insertion, not regular approval actions
          throw new Error('Add approvers action should use insertAdditionalApprover method');

        default:
          throw new Error(`Unknown action type: ${request.action}`);
      }

      return {
        success: true,
        data: {
          success: true,
          message: `Action '${request.action}' completed successfully`,
          newStatus: 'Updated',
        },
        message: `Action '${request.action}' completed successfully`,
      };
    } catch (error: any) {
      console.error(`Failed to execute action '${request.action}':`, error);

      // Extract actual error message from API response
      const errorMessage =
        error?.response?.data?.message ||
        error?.response?.data?.errors?.[0] ||
        error?.message ||
        'Failed to execute action';

      return {
        success: false,
        data: {
          success: false,
          message: errorMessage,
          newStatus: 'Error',
        },
        message: errorMessage,
      };
    }
  }

  /**
   * Get current user's approval position for a ticket
   * NOTE: Backend endpoint not implemented yet - returns default values
   */
  async getApprovalPosition(_ticketId: ID): Promise<ApiResponse<ApprovalPosition>> {
    // Return default approval position (endpoint not implemented)
    return {
      success: true,
      data: {
        level: 1,
        levelNumber: 1,
        levelName: 'Level 1 Approval',
        isCurrentLevel: true,
        hasActioned: false,
        canTakeAction: true,
        isFinalApprover: false,
        isDynamicLevel: false,
      },
      message: 'Default approval position',
    };
  }

  /**
   * Get related tickets for a ticket
   * NOTE: Backend endpoint not implemented yet - returns empty array
   */
  async getRelatedTickets(_ticketId: ID): Promise<ApiResponse<RelatedTicketDisplay[]>> {
    // Return empty array (endpoint not implemented)
    return {
      success: true,
      data: [],
      message: 'No related tickets',
    };
  }

  /**
   * Get approval levels with dynamic insertion info
   * NOTE: Backend endpoint not implemented yet - returns empty array
   */
  async getApprovalLevelsWithDynamic(_ticketId: ID): Promise<ApiResponse<ApprovalLevelWithDynamic[]>> {
    // Return empty array (endpoint not implemented)
    return {
      success: true,
      data: [],
      message: 'No approval levels',
    };
  }

  // ============================================================================
  // DYNAMIC APPROVAL INSERTION
  // ============================================================================

  /**
   * Insert a single additional approver
   */
  async insertAdditionalApprover(
    request: InsertAdditionalApproverRequest
  ): Promise<ApiResponse<InsertAdditionalApproverResponse>> {
    const response = await post<ApiResponse<InsertAdditionalApproverResponse>>(
      `/v1/das/tickets/${request.ticketId}/additional-approver`,
      request
    );
    return response.data;
  }

  /**
   * Insert multiple additional approvers (sequential or parallel)
   */
  async insertMultipleAdditionalApprovers(
    request: InsertMultipleAdditionalApproversRequest
  ): Promise<ApiResponse<InsertMultipleAdditionalApproversResponse>> {
    const response = await post<ApiResponse<InsertMultipleAdditionalApproversResponse>>(
      `/v1/das/tickets/${request.ticketId}/additional-approvers-multiple`,
      request
    );
    return response.data;
  }

  /**
   * Process dynamic approval (approve/reject dynamically inserted level)
   */
  async processDynamicApproval(
    request: ProcessDynamicApprovalRequest
  ): Promise<ApiResponse<TakeActionResponse>> {
    const response = await post<ApiResponse<TakeActionResponse>>(
      `/v1/das/tickets/${request.ticketId}/process-dynamic-approval`,
      request
    );
    return response.data;
  }

  /**
   * Get additional approvers for a ticket
   */
  async getAdditionalApprovers(ticketId: ID): Promise<ApiResponse<AdditionalApprover[]>> {
    const response = await get<ApiResponse<AdditionalApprover[]>>(
      `/v1/das/tickets/${ticketId}/additional-approvers`
    );
    return response.data;
  }

  // ============================================================================
  // WORKFLOW & HISTORY
  // ============================================================================

  /**
   * Get workflow history for a ticket
   */
  async getWorkflowHistory(id: ID): Promise<ApiResponse<WorkflowAction[]>> {
    const response = await get<ApiResponse<WorkflowAction[]>>(
      dasTicketEndpoints.getWorkflowHistory(id)
    );
    return response.data;
  }

  /**
   * Get approval hierarchy for a ticket
   */
  async getApprovalHierarchy(id: ID): Promise<ApiResponse<ApprovalLevel[]>> {
    const response = await get<ApiResponse<ApprovalLevel[]>>(
      dasTicketEndpoints.getApprovalHierarchy(id)
    );
    return response.data;
  }

  // ============================================================================
  // STATISTICS & DASHBOARD
  // ============================================================================

  /**
   * Get dashboard statistics
   */
  async getDashboardStats(): Promise<ApiResponse<DashboardStats>> {
    const response = await get<ApiResponse<DashboardStats>>(dasTicketEndpoints.getDashboardStats());
    return response.data;
  }

  /**
   * Get current user's statistics
   */
  async getMyStats(): Promise<ApiResponse<DashboardStats>> {
    const response = await get<ApiResponse<DashboardStats>>(dasTicketEndpoints.getMyStats());
    return response.data;
  }

  // ============================================================================
  // SEARCH
  // ============================================================================

  /**
   * Search tickets
   */
  async search(params: DASTicketFilterParams): Promise<ListResponse<DASTicketListItem>> {
    const endpoint = buildEndpointWithQuery(dasTicketEndpoints.search(), params);
    const response = await get<ListResponse<DASTicketListItem>>(endpoint);
    return response.data;
  }
}

// Export singleton instance
export const dasTicketService = new DASTicketService();
export default dasTicketService;
